// Sacred Design System for Amaspa - Where Technology Meets Soul
export const colors = {
  // Primary Sacred Palette - New Earthy Tones
  primary: {
    50: '#f9f7f4',
    100: '#f0ebe3',
    200: '#e6dcc8',
    300: '#dcd0b8', // Core light beige
    400: '#c4b59a',
    500: '#a89977',
    600: '#8b7356', // Core medium brown
    700: '#6f5a42',
    800: '#5a4c39', // Core dark brown
    900: '#4a3f30',
  },
  
  // Secondary warm tones derived from the palette
  secondary: {
    50: '#faf8f6',
    100: '#f2ede7',
    200: '#e8ddd1',
    300: '#d4c4b0',
    400: '#b8a084',
    500: '#9d7f5f',
    600: '#8b7356',
    700: '#6b5640',
    800: '#5a4c39',
    900: '#453a2b',
  },
  
  // Accent earth tones
  accent: {
    50: '#faf9f7',
    100: '#f4f1ec',
    200: '#ebe5db',
    300: '#dcd0b8',
    400: '#c7b89e',
    500: '#b0a082',
    600: '#968166',
    700: '#7a6650',
    800: '#5a4c39',
    900: '#463c2e',
  },
  
  // Complementary warm grays
  neutral: {
    50: '#faf9f8',
    100: '#f3f2f0',
    200: '#e9e7e3',
    300: '#dcd0b8',
    400: '#b8aca0',
    500: '#968a7e',
    600: '#7a6f63',
    700: '#5a4c39',
    800: '#4a3f30',
    900: '#3a3025',
  },
  
  // Vibrant Teal/Turquoise Palette - Inspired by EmerV Sacred Portal
  tiffany: {
    50: '#ecfeff',
    100: '#cffafe', 
    200: '#a5f3fc',
    300: '#67e8f9',  // Light vibrant teal
    400: '#22d3ee',  // Bright turquoise
    500: '#06b6d4',  // Core vibrant teal blue
    600: '#0891b2',  // Medium vibrant teal
    700: '#0e7490',  // Deep vibrant teal
    800: '#155e75',
    900: '#164e63',
  },

  // Neutral Sacred Tones - Updated to match new palette
  sacred: {
    white: '#fefefe',
    cream: '#faf9f7',
    whisper: '#f4f1ec',
    mist: '#e9e7e3',
    stone: '#dcd0b8',
    sage: '#b8aca0',
    shadow: '#968a7e',
    earth: '#5a4c39',
    night: '#4a3f30',
    void: '#3a3025',
    // Vibrant teal accents for sacred elements
    tiffanyMist: '#ecfeff',
    tiffanyGlow: '#06b6d4',
    tiffanyDeep: '#0e7490',
  }
};

export const typography = {
  fonts: {
    serif: '"Crimson Text", "Georgia", "Times New Roman", serif',
    sans: '"Inter", "Helvetica Neue", "Arial", sans-serif',
    display: '"Playfair Display", "Georgia", serif',
  },
  
  sizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
    '6xl': '3.75rem', // 60px
    '7xl': '4.5rem',  // 72px
  },
  
  weights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  
  lineHeights: {
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
  
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  }
};

export const spacing = {
  px: '1px',
  0: '0',
  0.5: '0.125rem',  // 2px
  1: '0.25rem',     // 4px
  1.5: '0.375rem',  // 6px
  2: '0.5rem',      // 8px
  2.5: '0.625rem',  // 10px
  3: '0.75rem',     // 12px
  3.5: '0.875rem',  // 14px
  4: '1rem',        // 16px
  5: '1.25rem',     // 20px
  6: '1.5rem',      // 24px
  7: '1.75rem',     // 28px
  8: '2rem',        // 32px
  9: '2.25rem',     // 36px
  10: '2.5rem',     // 40px
  11: '2.75rem',    // 44px
  12: '3rem',       // 48px
  14: '3.5rem',     // 56px
  16: '4rem',       // 64px
  20: '5rem',       // 80px
  24: '6rem',       // 96px
  28: '7rem',       // 112px
  32: '8rem',       // 128px
  36: '9rem',       // 144px
  40: '10rem',      // 160px
  44: '11rem',      // 176px
  48: '12rem',      // 192px
  52: '13rem',      // 208px
  56: '14rem',      // 224px
  60: '15rem',      // 240px
  64: '16rem',      // 256px
  72: '18rem',      // 288px
  80: '20rem',      // 320px
  96: '24rem',      // 384px
};

export const borderRadius = {
  none: '0',
  sm: '0.125rem',   // 2px
  DEFAULT: '0.25rem', // 4px
  md: '0.375rem',   // 6px
  lg: '0.5rem',     // 8px
  xl: '0.75rem',    // 12px
  '2xl': '1rem',    // 16px
  '3xl': '1.5rem',  // 24px
  full: '9999px',
};

export const shadows = {
  whisper: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  gentle: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  soft: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  flowing: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  sacred: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
};

export const transitions = {
  DEFAULT: 'all 150ms ease',
  colors: 'color 150ms ease, background-color 150ms ease, border-color 150ms ease',
  transform: 'transform 150ms ease',
  opacity: 'opacity 150ms ease',
  shadow: 'box-shadow 150ms ease',
  gentle: 'all 300ms ease-out',
  flowing: 'all 500ms ease-in-out',
  sacred: 'all 800ms cubic-bezier(0.4, 0, 0.2, 1)',
};

export const breakpoints = {
  sm: '640px',
  md: '768px', 
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

// Sacred Animation Curves
export const easings = {
  gentle: [0.25, 0.46, 0.45, 0.94],
  flowing: [0.4, 0, 0.2, 1],
  sacred: [0.645, 0.045, 0.355, 1],
  breath: [0.23, 1, 0.32, 1],
  whisper: [0.175, 0.885, 0.32, 1.275],
};

// Micro-interaction presets
export const microInteractions = {
  ripple: {
    scale: [1, 1.05, 1],
    transition: { duration: 0.3, ease: easings.gentle }
  },
  glow: {
    opacity: [0.7, 1, 0.7],
    transition: { duration: 2, repeat: Infinity, ease: easings.breath }
  },
  shimmer: {
    x: [-100, 100],
    transition: { duration: 1.5, ease: easings.flowing }
  },
  breathe: {
    scale: [1, 1.02, 1],
    transition: { duration: 4, repeat: Infinity, ease: easings.breath }
  }
}; 
